import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { alpha, Box, Card, CardActionArea, CardContent, CardMedia, Typography, useTheme } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useLocale } from 'hooks';
import config from 'config';
import VideoDialog from './VideoDialog';
import { Video } from 'models/video.model';

const VideoCard: FC<{ video: Video }> = ({ video }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const locale = useLocale();

  // Get the title based on locale
  const title = useMemo(() => {
    const titleKey = `title_${locale}` as keyof Video;
    return (video[titleKey] as string) || video.title_fr || video.name;
  }, [video, locale]);

  const thumbnailSrc = useMemo(() => {
    if (!video.thumbnail) return '';
    if (video.thumbnail.startsWith('data:')) {
      return video.thumbnail;
    }
    let imageType = 'jpeg';
    if (video.thumbnail.startsWith('iVBORw0KGgo')) {
      imageType = 'png';
    } else if (video.thumbnail.startsWith('/9j/') || video.thumbnail.startsWith('/9j/4AAQ')) {
      imageType = 'jpeg';
    }
    return `data:image/${imageType};base64,${video.thumbnail}`;
  }, [video.thumbnail]);

  const videoUrl = useMemo(() => {
    if (!video.path) return '';
    if (video.path.startsWith('http://') || video.path.startsWith('https://')) {
      return video.path;
    }
    const apiUrl = config.websiteBionextTVApiUrl || '';
    const baseUrl = apiUrl.replace(/\/$/, '');
    const path = video.path.startsWith('/') ? video.path : `/${video.path}`;
    return `${baseUrl}${path}`;
  }, [video.path]);

  return (
    <>
      <Card
        sx={{
          padding: { xs: 0, sm: 0 },
          borderRadius: '0px',
          boxShadow: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <CardContent>
          <CardActionArea
            onClick={() => setOpen(true)}
            sx={{
              '&:hover, &:focus': {
                opacity: 0.8,
              },
            }}
          >
            <Box position='relative' display='flex' justifyContent='center' alignItems='center'>
              <PlayCircleOutlineIcon
                sx={{
                  position: 'absolute',
                  width: '50%',
                  height: '50%',
                  color: alpha(theme.palette.common.white, 0.8),
                  zIndex: 1,
                }}
              />
              <CardMedia
                component='img'
                image={thumbnailSrc}
                alt={title}
                sx={{
                  width: '100%',
                  height: 'auto',
                  aspectRatio: '16/9',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </CardActionArea>
          <Box py={1}>
            <Typography variant='subtitle1'>{title}</Typography>
          </Box>
        </CardContent>
      </Card>
      <VideoDialog video={{ video: videoUrl, thumbnail: thumbnailSrc }} open={open} setOpen={setOpen} />
    </>
  );
};

export default VideoCard;
